import type jsPDF from 'jspdf';
import type { CellHookData, UserOptions } from 'jspdf-autotable';

import Constants from '@/constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Plan-description footnote source row (marker assigned during assembly). */
export interface PrintReportFootnote {
  planId: string;
  marker: string;
  planName: string;
  description: string;
}

/** Marker and description rendered in the print report footer. */
export type PrintReportFootnoteDisplay = Pick<
  PrintReportFootnote,
  'marker' | 'description'
>;

/** Normalized report row used when building print tables and footnotes. */
export type PrintReportRow = Record<string, string | undefined> & {
  planId?: string;
  planName?: string;
  planType?: string;
  planDescription?: string;
};

/** Footnotes plus a resolver that appends superscript markers to plan names. */
export interface PrintReportFootnotesResult {
  footnotes: PrintReportFootnote[];
  resolvePlanName: (row: PrintReportRow) => string;
}

/**
 * Unicode superscript digits. jsPDF does not map U+2074+ to digits, so we
 * must convert manually when drawing PDF output.
 */
const SUPERSCRIPT_DIGIT: Record<string, string> = {
  '0': '\u2070',
  '1': '\u00B9',
  '2': '\u00B2',
  '3': '\u00B3',
  '4': '\u2074',
  '5': '\u2075',
  '6': '\u2076',
  '7': '\u2077',
  '8': '\u2078',
  '9': '\u2079',
};

const DIGIT_BY_SUPERSCRIPT = Object.fromEntries(
  Object.entries(SUPERSCRIPT_DIGIT).map(([digit, superscript]) => [
    superscript,
    digit,
  ]),
) as Record<string, string>;

const TRAILING_SUPERSCRIPT_MARKER_PATTERN = new RegExp(
  `([${Object.values(SUPERSCRIPT_DIGIT).join('')}]+)$`,
  'u',
);

const toSuperscriptDigits = (
  value: number,
  digitMap: Record<string, string>,
): string =>
  String(value)
    .split('')
    .map(digit => digitMap[digit] ?? digit)
    .join('');

/** Maps 1-based index to Unicode superscript marker (¹, ², …, ¹⁰, ¹¹, …). */
export const superscriptMarker = (index: number): string => {
  if (!Number.isInteger(index) || index < 1) {
    return '';
  }

  return toSuperscriptDigits(index, SUPERSCRIPT_DIGIT);
};

/** Converts a superscript marker string back to plain digits (e.g. ¹⁰ → "10"). */
export const superscriptMarkerToDigits = (marker: string): string => {
  let digits = '';
  for (const char of marker) {
    digits += DIGIT_BY_SUPERSCRIPT[char] ?? '';
  }
  return digits;
};

/** Splits a trailing superscript suffix from display text. */
export const splitTrailingSuperscriptMarker = (
  value: unknown,
): { text: string; marker: string } | null => {
  const raw = typeof value === 'string' ? value : '';
  const match = TRAILING_SUPERSCRIPT_MARKER_PATTERN.exec(raw);
  const superscriptMarker = match?.[1];
  if (!superscriptMarker) {
    return null;
  }

  const marker = superscriptMarkerToDigits(superscriptMarker);
  return marker
    ? {
        text: raw.slice(0, -superscriptMarker.length),
        marker,
      }
    : null;
};

/** Appends a superscript marker directly after the plan name. */
export const formatPlanNameWithFootnote = (
  planName: string,
  marker: string | undefined,
): string => (marker ? `${planName}${marker}` : planName);

const FOOTNOTE_LINE_HEIGHT = 5;
const FOOTNOTE_TOP_MARGIN = 10;
const FOOTNOTE_BOTTOM_MARGIN = 30;
const PDF_SUPERSCRIPT_FONT_RATIO = 0.65;
const PDF_SUPERSCRIPT_RAISE = 1.8;

/** Measures bold superscript marker width. */
const measurePdfSuperscriptMarkerWidth = (
  doc: jsPDF,
  marker: string,
  baseFontSize = 9,
): number => {
  const font = doc.getFont();
  const fontSize = doc.getFontSize();
  const superSize = baseFontSize * PDF_SUPERSCRIPT_FONT_RATIO;

  doc.setFont(font.fontName, 'bold');
  doc.setFontSize(superSize);
  const width = doc.getTextWidth(`${marker} `);
  doc.setFontSize(fontSize);
  doc.setFont(font.fontName, font.fontStyle);

  return width;
};

/** Draws superscript marker at specified position. */
const drawPdfSuperscriptMarker = (
  doc: jsPDF,
  marker: string,
  x: number,
  y: number,
  baseFontSize = 9,
): number => {
  const font = doc.getFont();
  const markerWidth = measurePdfSuperscriptMarkerWidth(
    doc,
    marker,
    baseFontSize,
  );
  const superSize = baseFontSize * PDF_SUPERSCRIPT_FONT_RATIO;

  doc.setFont(font.fontName, 'bold');
  doc.setFontSize(superSize);
  doc.text(marker, x, y - PDF_SUPERSCRIPT_RAISE);
  doc.setFontSize(baseFontSize);
  doc.setFont(font.fontName, font.fontStyle);

  return markerWidth;
};

/** Anchor point for drawing a superscript after the last line of a table cell. */
const planNameMarkerPositionFromCell = (
  data: CellHookData,
  text: string,
): { x: number; y: number } => {
  const doc = data.doc as unknown as jsPDF;
  const scaleFactor = doc.internal.scaleFactor;
  const fontSize = data.cell.styles.fontSize / scaleFactor;
  const fontName = data.cell.styles.font;
  const fontStyle = data.cell.styles.fontStyle;
  const lineHeightFactor = doc.getLineHeightFactor();
  const lineHeight = fontSize * lineHeightFactor;
  const lines = data.cell.text;
  const lineCount = lines.length || 1;

  doc.setFont(fontName, fontStyle);
  doc.setFontSize(data.cell.styles.fontSize);

  const textPos = data.cell.getTextPos();
  const alignOffset =
    data.cell.styles.halign === 'center'
      ? doc.getStringUnitWidth(text) * fontSize * 0.5
      : data.cell.styles.halign === 'right'
        ? doc.getStringUnitWidth(text) * fontSize
        : 0;
  const lineIndex = Math.max(lines.length - 1, 0);
  let y = textPos.y + fontSize * (2 - 1.15);
  if (data.cell.styles.valign === 'middle') {
    y -= (lineCount / 2) * lineHeight;
  } else if (data.cell.styles.valign === 'bottom') {
    y -= lineCount * lineHeight;
  }
  y += lineIndex * lineHeight;

  return {
    x: textPos.x - alignOffset + doc.getTextWidth(text),
    y,
  };
};

/**
 * jspdf-autotable hooks: strip trailing superscript from parsed text, then
 * redraw the marker in PDF-safe typography on the plan-name column.
 */
export const pdfPlanNameSuperscriptHooks: Pick<
  UserOptions,
  'didParseCell' | 'didDrawCell'
> = {
  didParseCell: data => {
    if (data.section !== 'body' || data.column.index !== 0) {
      return;
    }

    const parsed = splitTrailingSuperscriptMarker(data.cell.raw);
    if (parsed) {
      data.cell.text = [parsed.text];
    }
  },
  didDrawCell: data => {
    if (data.section !== 'body' || data.column.index !== 0) {
      return;
    }

    const parsed = splitTrailingSuperscriptMarker(data.cell.raw);
    if (!parsed) {
      return;
    }

    const lastLine = data.cell.text.at(-1) ?? parsed.text;
    const { x, y } = planNameMarkerPositionFromCell(data, lastLine);
    drawPdfSuperscriptMarker(
      data.doc as unknown as jsPDF,
      parsed.marker,
      x,
      y,
      data.cell.styles.fontSize,
    );
  },
};

/** Renders plan-description footnotes and training-provider line. */
export const renderPrintReportFootnotes = (
  doc: jsPDF,
  footnotes: PrintReportFootnoteDisplay[],
  trainingProviderLine: string | undefined,
) => {
  if (!footnotes.length && !trainingProviderLine) {
    return;
  }

  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const marginX = 14;
  let y =
    ((doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? 25) + FOOTNOTE_TOP_MARGIN;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - FOOTNOTE_BOTTOM_MARGIN) {
      doc.addPage();
      y = 20;
    }
  };

  footnotes.forEach(footnote => {
    const marker =
      superscriptMarkerToDigits(footnote.marker) || footnote.marker;
    const markerWidth = measurePdfSuperscriptMarkerWidth(doc, marker);
    doc.setFont(doc.getFont().fontName, 'normal');
    doc.setFontSize(9);
    const wrappedLines = doc.splitTextToSize(
      footnote.description,
      pageWidth - marginX * 2 - markerWidth,
    ) as string | string[];
    const wrapped = Array.isArray(wrappedLines) ? wrappedLines : [wrappedLines];
    const blockHeight = wrapped.length * FOOTNOTE_LINE_HEIGHT + 4;
    ensureSpace(blockHeight);
    drawPdfSuperscriptMarker(doc, marker, marginX, y);
    doc.text(wrapped, marginX + markerWidth, y);
    y += blockHeight;
  });

  if (trainingProviderLine) {
    ensureSpace(FOOTNOTE_LINE_HEIGHT + 8);
    y += 4;
    doc.setFont(doc.getFont().fontName, 'normal');
    doc.setFontSize(9);
    doc.text(trainingProviderLine, marginX, y);
  }
};

// ---------------------------------------------------------------------------
// Report tables
// ---------------------------------------------------------------------------

/** Rows shown in the compliance summary table (in-house compliance plans). */
export const isComplianceTableRow = (row: PrintReportRow): boolean =>
  row.planType === 'COMPLIANCE';

/** Splits rows into compliance vs store tables (compliance rows render first). */
export const splitPrintReportRows = (rows: PrintReportRow[]) => {
  const complianceReport = rows.filter(isComplianceTableRow);
  const storeReport = rows.filter(row => !isComplianceTableRow(row));

  return { complianceReport, storeReport };
};

/** Column values for the compliance summary autotable body. */
export const buildComplianceTableBody = (
  rows: PrintReportRow[],
  resolvePlanName: (row: PrintReportRow) => string,
) =>
  rows.map(row => [
    resolvePlanName(row),
    row[Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN],
    row[Constants.REPORT_TABLE.COMPLETION_DATE],
    row[Constants.REPORT_TABLE.EXPIRATION_DATE],
  ]);

/** Column values for the store-plan autotable body. */
export const buildStoreTableBody = (
  rows: PrintReportRow[],
  resolvePlanName: (row: PrintReportRow) => string,
) =>
  rows.map(row => [
    resolvePlanName(row),
    row[Constants.REPORT_TABLE.ITEMS_COMPLETED],
    row[Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN],
    row[Constants.REPORT_TABLE.STATUS],
    row[Constants.REPORT_TABLE.COMPLETION_DATE],
  ]);

// ---------------------------------------------------------------------------
// Footnote assembly
// ---------------------------------------------------------------------------

const printReportPlanNameFromRow = (row: PrintReportRow) =>
  row.planName ?? row[Constants.REPORT_TABLE.PLAN] ?? '';

/** No-op footnotes for users without footnotes. */
export const emptyPrintReportFootnotesResult: PrintReportFootnotesResult = {
  footnotes: [],
  resolvePlanName: printReportPlanNameFromRow,
};

/** Builds sequential superscript footnotes from plan descriptions. */
export const buildPrintReportFootnotes = (
  orderedRows: PrintReportRow[],
): PrintReportFootnotesResult => {
  const markerByPlanId = new Map<string, string>();
  const footnotes: PrintReportFootnote[] = [];
  let markerIndex = 1;

  for (const row of orderedRows) {
    const planId = row.planId;
    const description = row.planDescription?.trim();

    if (!planId || !description) {
      continue;
    }

    const marker = superscriptMarker(markerIndex);
    markerIndex += 1;
    markerByPlanId.set(planId, marker);
    footnotes.push({
      planId,
      marker,
      planName: printReportPlanNameFromRow(row),
      description,
    });
  }

  return {
    footnotes,
    resolvePlanName: row => {
      const marker = row.planId ? markerByPlanId.get(row.planId) : undefined;

      return formatPlanNameWithFootnote(
        printReportPlanNameFromRow(row),
        marker,
      );
    },
  };
};
